import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'
import styled from 'styled-components'
import Dialog from '../../dialog'
import Button from '../../button'
import Select from '../../select'
import { HeadLine3 } from '../../typography/styled'
import dedicatedServersService from '../../../api/dedicatedServersService'
import dedicatedPreordersService, { DedicatedPreorder } from '../../../api/dedicatedPreordersService'
import { DedicatedServer } from '../../../pages/dedicated-servers/types'
import { canReleasePreorder, preorderError } from '../../../pages/dedicated-servers/preorders/helpers'
import { ErrorMessage, FormActions } from '../../../pages/dedicated-servers/preorder-hardware/styles'
import { Note } from '../../../pages/dedicated-servers/preorders/styles'
import { formatHardwareSize } from '../../../pages/dedicated-servers/hardware'
import useDebounce from '../../../hooks/useDebounce'

const Content = styled.div`
  width: min(600px, calc(100vw - 32px));
  padding: 24px;
  box-sizing: border-box;
  border-radius: 8px;
  background: ${({ theme }) => theme.palette[10]};
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const ServerInfo = styled.div`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.grey[350]};
  border-radius: 6px;
`

type Props = {
  preorder: DedicatedPreorder;
  onClose: () => void;
  onReleased: (operationId: string) => void;
}

const serverLabel = (server: DedicatedServer) => (
  `${server.name || server.servername || server.hostname || server.id} · #${server.external_id}${server.hostname && server.hostname !== server.name ? ` · ${server.hostname}` : ''}`
)

const ReleaseDedicatedPreorder: FC<Props> = ({ preorder, onClose, onReleased }) => {
  const [t] = useTranslation()
  const [servers, setServers] = useState<DedicatedServer[]>([])
  const [selected, setSelected] = useState<DedicatedServer>()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const busy = useRef(false)
  const close = useCallback(() => { if (!busy.current) onClose() }, [onClose])

  useEffect(() => {
    const overflow = document.body.style.overflowY
    document.body.style.overflowY = 'hidden'
    return () => { document.body.style.overflowY = overflow }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    let retry: number | undefined
    const load = async () => {
      setLoading(true)
      try {
        const { data } = await dedicatedServersService.getServers({
          is_assigned: false,
          deleted: false,
          external_tags: 'preorder,free',
          search: debouncedSearch || undefined,
          page,
          page_size: 20,
        }, controller.signal)
        if (controller.signal.aborted) return
        const objects: DedicatedServer[] = data.objects ?? data.results ?? []
        setServers((previous) => {
          const combined = page === 1 ? objects : [...previous, ...objects]
          return [...new Map(combined.map((server) => [server.id, server])).values()]
        })
        setHasMore(Boolean(data.next) || page * 20 < (data.count ?? data.totalCount ?? 0))
        setLoadError('')
      } catch (error) {
        if (controller.signal.aborted) return
        setLoadError(preorderError(error, String(t('PREORDERS_SERVERS_LOAD_FAILED'))))
        retry = window.setTimeout(load, 10000)
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    load()
    return () => { controller.abort(); window.clearTimeout(retry) }
  }, [debouncedSearch, page, t])

  const formik = useFormik({
    initialValues: { server_id: '' },
    validationSchema: yup.object({ server_id: yup.string().required(String(t('PREORDER_REQUIRED'))) }),
    onSubmit: async ({ server_id }, helpers) => {
      if (busy.current || !selected || !canReleasePreorder(preorder)) return
      busy.current = true
      helpers.setStatus(undefined)
      try {
        const result = await dedicatedPreordersService.release(preorder.id, server_id)
        onReleased(result.operation_id)
      } catch (error) {
        helpers.setStatus(preorderError(error, String(t('ERROR_OCCURRED'))))
      } finally {
        busy.current = false
      }
    },
  })

  const choices = [...new Map([...(selected ? [selected] : []), ...servers].map((server) => [server.id, server])).values()]

  return (
    <Dialog open active noIframe handleClose={close}>
      <Content role="dialog" aria-modal="true" aria-labelledby="release-preorder-title" data-testid="release-preorder-dialog">
        <HeadLine3 id="release-preorder-title">{t('PREORDERS_RELEASE')}</HeadLine3>
        <Note>{t('PREORDERS_RELEASE_CONFIRM', { name: preorder.name || preorder.id, user: preorder.dedicated_user?.external_id ?? '-' })}</Note>
        <form onSubmit={formik.handleSubmit} aria-label={String(t('PREORDERS_RELEASE'))} noValidate>
          <Select
            title={t('PREORDERS_AVAILABLE_SERVER')}
            placeholder={loading ? t('LOADING') : t('CHOOSE')}
            data={choices.map((server) => ({ value: server.id, label: serverLabel(server) }))}
            selectedValue={formik.values.server_id}
            onChange={(id) => {
              setSelected(choices.find((server) => server.id === id))
              formik.setFieldValue('server_id', id)
            }}
            hasSearch
            onSearchChange={(value) => { setPage(1); setSearch(value) }}
            fieldName="server_id"
            errors={formik.errors}
            touched={formik.touched}
            disabled={formik.isSubmitting}
            required
            test_id="release-server-select"
          />
          {loading && <Note role="status">{t('LOADING')}</Note>}
          {!loading && !loadError && !servers.length && <Note>{t('PREORDERS_NO_AVAILABLE_SERVERS')}</Note>}
          {loadError && <ErrorMessage role="alert">{loadError}</ErrorMessage>}
          {hasMore && <Button variant="stroke" size="small" title={t('PREORDERS_LOAD_MORE_SERVERS')} disabled={loading || formik.isSubmitting} onClick={() => setPage((value) => value + 1)} />}
          {selected && (
            <ServerInfo>
              <Note>{serverLabel(selected)}</Note>
              <Note>ID: {selected.id}</Note>
              <Note>CPU: {selected.hardware?.cpu ? `${selected.hardware.cpu.count} × ${selected.hardware.cpu.model}` : '-'}</Note>
              <Note>{t('RAM')}: {formatHardwareSize(selected.hardware?.ram?.value, selected.hardware?.ram?.value_type)}</Note>
              <Note>{t('DISK')}: {formatHardwareSize(selected.hardware?.disk?.value, selected.hardware?.disk?.value_type)}</Note>
            </ServerInfo>
          )}
          {formik.status && <ErrorMessage role="alert">{formik.status}</ErrorMessage>}
          <FormActions>
            <Button type="submit" variant="primary" size="display" title={t('PREORDERS_RELEASE')} loading={formik.isSubmitting} disabled={!selected || !canReleasePreorder(preorder)} />
            <Button variant="stroke" size="display" title={t('CANCEL')} onClick={close} disabled={formik.isSubmitting} />
          </FormActions>
        </form>
      </Content>
    </Dialog>
  )
}

export default ReleaseDedicatedPreorder
